/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import * as vkbeautify from 'vkbeautify';
import fetch from 'isomorphic-fetch';
import { get } from 'lodash';

/** Sample invoke config
 export interface InvokeConfig {
    repeater?: {
        stopOnError: boolean;
        repeat: boolean;
        count: number;
    };
    verb: string;
    operationRef?: any;
    operationPath: string;
    targetUrl?: string;
    headers?: any;
    body?: any;
    scheme: string;
    contentTypeHeader: string;
    acceptHeader: string;
    username: string;
    password: string;
    clientId: string;
    clientSecret: string;
    authToken?: {
        'access_token': string;
    };
    parameterValues: any;
    apiKeys: any;
    proxyUrl?: string;
};*/

export default class Invoker {
  constructor(http) {
    this.invokeConfig = {};

    this.response = {};

    this.stopInvoke = false;
    this.invokeRunning = false;
  }

  async invokeHelperResponseHandler(invokeConfig, response, startTime, callCount, repeatCount, totalTime, request, isError, callback, resolve, reject) {
    let responseTime = new Date().getTime();
    callCount++;
    repeatCount--;
    let callAgain = repeatCount > 0 && (!isError || (isError && invokeConfig.repeater.stopOnError === false));

    if (response instanceof Error) {
      this.response.responseData = response;
      this.response.responseDataString = await response.toString();

      if (!callAgain) {
        this.invokeRunning = false;
        callback(this.response);
      }
      return;
    }
    this.response.statusCode = response.status;
    this.response.statusText = response.statusText;
    let elapsedTime = responseTime - startTime;
    this.response.responseTime = elapsedTime;
    totalTime += elapsedTime;
    if (callCount + repeatCount > 1) {
      this.response.avgResponseTime = Math.round(totalTime / callCount);
      this.response.callCounter = callCount;
      this.response.totalTime = totalTime;
    }

    if (response.headers.get('content-disposition') && response.headers.get('content-disposition').indexOf('attachment') !== -1) {
      this.response.responseDataString = await response.blob();
      this.response.isFileDownload = true;
    } else  if (response.headers.get('content-type')) {
      let contentType = response.headers.get('content-type');
      let responseData;
      if (contentType.indexOf('json') !== -1) {
        responseData = await response.text();
        try {
          this.response.responseDataString = JSON.stringify(JSON.parse(responseData), null, 2);
        } catch (error) {
          console.error(error);
          this.response.responseDataString = responseData;
        }
      } else if (contentType.indexOf('pdf') !== -1 || contentType.indexOf('zip') !== -1 || contentType.indexOf('image') !== -1 ) {
        this.response.responseDataString = await response.blob();
        this.response.isFileDownload = true
      } else {
        responseData = await response.text();
        if (responseData.startsWith('<')) {
          try {
            this.response.responseDataString = vkbeautify ? vkbeautify.xml(responseData) : responseData;
          } catch (e) {
            this.response.responseDataString = responseData;
          }
        } else {
          try {
            this.response.responseDataString = JSON.stringify(JSON.parse(responseData), null, 2);
          } catch (error) {
            console.error(error);
            this.response.responseDataString = responseData;
          }
        }
      }
    }

    if (this.stopInvoke) {
      this.stopInvoke = false;
      this.invokeRunning = false;
      callback(this.response);
      return;
    }

    let headersAsObject = {};
    let headersAsString = '';
    if (response.headers) {
      response.headers.forEach(function (value, name) {
        headersAsString += name + ': ' + value + '\n';
        headersAsObject[name] = value;
      });
    }

    this.response.headers = headersAsObject;
    if (Object.keys(this.response.headers).length > 0) {
      this.response.containsHeaders = true;
    }

    this.response.transactionId = this.response.headers['apim-debug-trans-id'];
    this.response.headersAsString = headersAsString;

    // provide details of the request too
    let requestHeadersAsString = '';
    Object.keys(request.headers).forEach(function (headerName) {
      requestHeadersAsString += headerName + ': ' + request.headers[headerName] + '\n';
    });

    this.response.request = {
      url: request.url,
      method: request.method,
      headers: request.headers,
      headersAsString: requestHeadersAsString
    };

    if (invokeConfig.proxyUrl) {
      this.response.request.url = invokeConfig.targetUrl;
      this.response.request.method = invokeConfig.operationRef ? invokeConfig.operationRef.verb : invokeConfig.verb;
    }

    if (callAgain) {
      this.invokeHelper(invokeConfig, callCount, repeatCount, totalTime, resolve, reject);
    } else {
      this.invokeRunning = false;
      callback(this.response);
    }
  }

  deepObjectHelper(name, depth, value, list, currentValueToAdd, allowReserved) {
    for (const [key, val] of Object.entries(value)) {
      let valueToAdd = currentValueToAdd;
      if (depth === 1) {
        if (allowReserved === true) {
          valueToAdd += name + '%5B' + key + '%5D';
        } else {
          valueToAdd += name + '%5B' + encodeURIComponent(key) + '%5D';
        }
      } else {
        if (allowReserved === true) {
          valueToAdd += '%5B' + key + '%5D';
        } else {
          valueToAdd += '%5B' + encodeURIComponent(key) + '%5D';
        }
      }
      if (typeof val === 'object' && val !== null) {
        this.deepObjectHelper(name, depth + 1, val, list, valueToAdd);
      } else {
        if (allowReserved === true) {
          valueToAdd += '=' + val;
        } else {
          valueToAdd += '=' + encodeURIComponent(val);
        }
        list.push(valueToAdd);
      }
    }
    return list;
  }

  convertParameterValue(parameter, type, version) {
    let result = parameter.value;
    if (result === '__omit') {
      result = '';
    }
    if (parameter.value !== undefined && parameter.value !== '' && parameter.value !== null && parameter.value !== 'null' && parameter.value !== '__omit') {
      let value = parameter.value;
      let name = parameter.name;
      if (type === 'REST' && version && version.startsWith('3')) {
        let schema = parameter;
        if (schema.schema) {
          schema = schema.schema;
        }
        if (parameter.in === 'formData') {
          if (parameter.encoding) {
            var style = parameter.encoding.style;
            var explode = parameter.encoding.explode;
            var allowReserved = parameter.encoding.allowReserved;
          }
        } else {
          style = parameter.style;
          explode = parameter.explode;
          allowReserved = parameter.allowReserved;
        }
        //Should query names be encoded all the time or just when allowReserved = false????
        if (parameter.in === 'query') {
          name = encodeURIComponent(name);
        }
        if (parameter.content) {
          if (parameter.in === 'query' && allowReserved !== true) {
            result = name + '=' + encodeURIComponent(value);
          } else if (parameter.in === 'query') {
            result = name + '=' + value;
          } else if (parameter.in === 'path') {
            result = encodeURIComponent(value);
          } else {
            result = value;
          }
        } else if (schema.type === 'array') {
          try {
            value = JSON.parse(value);
          } catch (exception) {
            return;
          }
          if (parameter.in === 'path' || (!allowReserved && (parameter.in === 'query' || parameter.in === 'formData'))) {
            value = value.map(function (val) {
              return encodeURIComponent(val);
            });
          }
          switch (parameter.in) {
            case 'query':
            case 'formData':
              // All working
              switch (style) {
                case 'spaceDelimited':
                  if (explode === true) {
                    //users?id=3&id=4&id=5
                    result = name + '=' + value.join('&' + name + '=');
                  } else {
                    // users?id=3%204%205
                    result = name + '=' + value.join('%20');
                  }
                  break;
                case 'pipeDelimited':
                  if (explode === true) {
                    //users?id=3&id=4&id=5
                    result = name + '=' + value.join('&' + name + '=');
                  } else {
                    // users?id=3,4,5
                    result = name + '=' + value.join('|');
                  }
                  break;
                default:
                case 'form':
                  if (explode === false) {
                    // users?id=3,4,5
                    result = name + '=' + value.join();
                  } else {
                    //users?id=3&id=4&id=5
                    result = name + '=' + value.join('&' + name + '=');
                  }
              }
              break;
            case 'path':
              //All working
              switch (style) {
                case 'matrix':
                  if (explode === true) {
                    result = ';' + name + '=' + value.join(';' + name + '=');
                  } else {
                    result = ';' + name + '=' + value.join();
                  }
                  break;
                case 'label':
                  result = '.' + value.join('.');
                  // Explode = false version could possibly be
                  // result = '.' + list.join(',');
                  // based on https://swagger.io/docs/specification/serialization/
                  // but following how the editor works
                  break;
                default:
                case 'simple':
                  result = value.join(',');
              }
              break;
            case 'header':
              // Style should always be 'simple'
              // Explode has no effect for arrays
              // X-MyHeader: 3,4,5
              result = value.join(',');
              break;
            case 'cookie':
              if (explode === false) {
                result = name + '=' + value.join();
              } else {
                result = name + '=' + value.join(';' + name + '=');
              }
              break;
            default:
              break;
          }
        } else if (schema.type === 'object') {
          try {
            value = JSON.parse(value);
          } catch (exception) {
            return;
          }
          switch (parameter.in) {
            case 'query':
              if (style === 'deepObject') {
                result = this.deepObjectHelper(name, 1, value, [], '', allowReserved);
                result = result.join('&');
              } else if (style === 'pipeDelimited') {
                let list = [];
                for (const [key, val] of Object.entries(value)) {
                  if (allowReserved === true) {
                    list = list.concat([key, val]);
                  } else {
                    list = list.concat([encodeURIComponent(key), encodeURIComponent(val)]);
                  }
                }
                result = name + '=' + list.join('|');
              } else if (style === 'spaceDelimited') {
                let list = [];
                for (const [key, val] of Object.entries(value)) {
                  if (allowReserved === true) {
                    list = list.concat([key, val]);
                  } else {
                    list = list.concat([encodeURIComponent(key), encodeURIComponent(val)]);
                  }
                }
                result = name + '=' + list.join('%20');
              } else {
                if (explode === false) {
                  let list = [];
                  for (const [key, val] of Object.entries(value)) {
                    if (allowReserved === true) {
                      list = list.concat([key, val]);
                    } else {
                      list = list.concat([encodeURIComponent(key), encodeURIComponent(val)]);
                    }
                  }
                  result = name + '=' + list.join();
                } else {
                  // BUG - Allows multiple query parameters with the same name in the url if there are multiple query parameters
                  // that use explode and their objects have the same properties. Would probably require a restructure
                  // to fix as each parameter is added independently to the url.
                  // E.g. queryParam1 = {"id": 1}, queryParam2 = {"id": 3} will add id=1&id=3 instead of just id=3
                  let list = [];
                  for (const [key, val] of Object.entries(value)) {
                    if (allowReserved === true) {
                      let pair = key + '=' + val;
                      list.push(pair);
                    } else {
                      let pair = encodeURIComponent(key) + '=' + encodeURIComponent(val);
                      list.push(pair);
                    }
                  }
                  result = list.join('&');
                }
              }
              break;
            case 'formData':
              if (allowReserved === true) {
                result = name + '=' + JSON.stringify(value);
              } else {
                result = encodeURIComponent(name) + '=' + encodeURIComponent(JSON.stringify(value));
              }
              break;
            case 'header':
              // ALL Working
              if (explode === false) {
                let list = [];
                for (const entry of Object.entries(value)) {
                  list = list.concat(entry);
                }
                result = list.join();
              } else {
                let list = [];
                for (const [key, val] of Object.entries(value)) {
                  let pair = key + '=' + val;
                  list.push(pair);
                }
                result = list.join();
              }
              break;
            case 'path':
              //All Working
              switch (style) {
                case 'matrix':
                  if (explode === true) {
                    let list = [];
                    for (const [key, val] of Object.entries(value)) {
                      let pair = encodeURIComponent(key) + '=' + encodeURIComponent(val);
                      list.push(pair);
                    }
                    result = ';' + list.join(';');
                  } else {
                    let list = [];
                    for (const [key, val] of Object.entries(value)) {
                      list = list.concat([encodeURIComponent(key), encodeURIComponent(val)]);
                    }
                    result = ';' + name + '=' + list.join();
                  }
                  break;
                case 'label':
                  if (explode === true) {
                    let list = [];
                    for (const [key, val] of Object.entries(value)) {
                      let pair = encodeURIComponent(key) + '=' + encodeURIComponent(val);
                      list.push(pair);
                    }
                    result = '.' + list.join('.');
                  } else {
                    let list = [];
                    for (const [key, val] of Object.entries(value)) {
                      list = list.concat([encodeURIComponent(key), encodeURIComponent(val)]);
                    }
                    result = '.' + list.join('.');
                  }
                  break;
                default:
                case 'simple':
                  if (explode === true) {
                    let list = [];
                    for (const [key, val] of Object.entries(value)) {
                      let pair = encodeURIComponent(key) + '=' + encodeURIComponent(val);
                      list.push(pair);
                    }
                    result = list.join();
                  } else {
                    let list = [];
                    for (const [key, val] of Object.entries(value)) {
                      list = list.concat([encodeURIComponent(key), encodeURIComponent(val)]);
                    }
                    result = list.join();
                  }
              }
              break;
            case 'cookie':
              if (explode === false) {
                let list = [];
                for (const entry of Object.entries(value)) {
                  list = list.concat(entry);
                }
                result = name + '=' + list.join();
              } else {
                let list = [];
                for (const [key, val] of Object.entries(value)) {
                  let pair = key + '=' + val;
                  list.push(pair);
                }
                result = list.join(';');
              }
              break;
            default:
              break;
          }
        } else if (schema.type === 'string' || schema.type === 'integer' || schema.type === 'number' || schema.type === 'boolean') {
          if (parameter.in === 'header') {
            result = value;
          } else if (parameter.in === 'path') {
            value = encodeURIComponent(value);
            result = value;
            if (style === 'label') {
              result = '.' + value;
            } else if (style === 'matrix') {
              result = ';' + name + '=' + value;
            }
          } else if (parameter.in === 'cookie') {
            result = name + '=' + value;
          } else if (parameter.in === 'query' || parameter.in === 'formData') {
            if (parameter.allowReserved === true) {
              result = name + '=' + value;
            } else {
              result = name + '=' + encodeURIComponent(value);
            }
          }
        }
      }
      else if (parameter.collectionFormat) {
        if (typeof value === 'string') {
          if (value.startsWith('[')) {
            try {
              value = JSON.parse(value);
            } catch (exception) {
              return;
            }
          } else {
            value = value.split(',');
          }
        }
        value = value.map(v => encodeURIComponent(v));
        let parameterName = encodeURIComponent(parameter.name);
        switch (parameter.collectionFormat) {
          case 'ssv':
            result = value.join(' ');
            break;
          case 'tsv':
            result = value.join('\t');
            break;
          case 'pipes':
            result = value.join('|');
            break;
          case 'multi':
            result = value.join('&'.concat(parameterName, '='));
            break;
          case 'csv':
          default:
            result = value.join();
        }
        if (parameter.in === 'query' || parameter.in === 'formData') {
          result = parameterName + '=' + result;
        }
      }
      else if (parameter.in === 'query' || parameter.in === 'formData') {
        result = encodeURIComponent(parameter.name) + '=' + encodeURIComponent(result);
      }
    } else if (parameter.value === '') {
      if (!parameter.allowEmptyValue) {
        return;
      } else if (parameter.in === 'query') {
        result = parameter.name + '=';
      }
    } else if (parameter.value === 'null' && (parameter.in === 'cookie' || parameter.in === 'query')) {
      result = parameter.name + '=null';
    }
    return result;
  }

  handleFile(parameter, file, contents) {
    if (!file || !contents) {
      return '';
    }
    let formBody = '';
    let addedContentDisposition = false;
    if (parameter.encoding && parameter.encoding.headers) {
      for (const [key, val] of Object.entries(parameter.encoding.headers)) {
        if (key.toLowerCase() !== 'content-type') {
          formBody += key + ': ' + val + '\n';
          if (key.toLowerCase() === 'content-disposition') {
            addedContentDisposition = true;
          }
        }
      }
    }

    if (!addedContentDisposition) {
      formBody += 'Content-Disposition: form-data; name="' + parameter.name + '"; filename="' + file.name + '"\n';
    }

    if (parameter.encoding && parameter.encoding.contentType) {
      formBody += 'Content-Type: ' + parameter.encoding.contentType + '\n';
    } else if (file.type) {
      formBody += 'Content-Type: ' + file.type + '\n';
    } else {
      formBody += 'Content-Type: text/plain\n';
    }
    formBody += '\n';
    formBody += contents;
    formBody += '\n';
    return formBody;
  }

  invokeHelper(invokeConfig, callCount, repeatCount, totalTime, resolve, reject) {
    if (repeatCount <= 0) {
      return;
    }
    let startTime = new Date().getTime();
    let verb = invokeConfig.operationRef ? invokeConfig.operationRef.verb : invokeConfig.verb;

    verb = verb.toUpperCase();
    let req = null;
    if (invokeConfig.proxyUrl) {
      // via proxy service
      let body = {
        method: verb,
        url: invokeConfig.targetUrl,
        headers: invokeConfig.headers
      };
      if (invokeConfig.body) {
        body.body = invokeConfig.body;
      }

      req = {
        method: 'POST',
        url: invokeConfig.proxyUrl,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body),
        cache: 'no-store'
      };
    } else {
      // direct request
      req = {
        method: verb,
        url: invokeConfig.targetUrl,
        headers: invokeConfig.headers,
        cache: 'no-store'
      };
      if (invokeConfig.body) {
        req['body'] = invokeConfig.body;
      }
    }

    fetch(req.url, req)
      .then((response) => {
        this.invokeHelperResponseHandler(invokeConfig, response, startTime, callCount, repeatCount, totalTime, req, false, resolve, resolve, reject);
      })
      // .then()
      .catch((err) => {
        // Fill in some defaults to aid error handling
        let response = {
          status: 0,
          statusText: '',
          headers: new Headers(),
          error: err
        };

        this.invokeHelperResponseHandler(invokeConfig, response, startTime, callCount, repeatCount, totalTime, req, true, reject, resolve, reject);
      });
  }

  fillInvokeConfig(invokeConfig, config) {
    // clear out any old response
    this.response = {};

    let operationPath = invokeConfig.operationPath;
    let endpoint = invokeConfig.endpoint;

    // set up headers
    let headers = {};
    if (config.sendDebugHeader) {
      headers['APIm-Debug'] = true;
    }
    if (invokeConfig.contentTypeHeader) {
      headers['Content-Type'] = invokeConfig.contentTypeHeader;
    }
    if (invokeConfig.acceptHeader) {
      headers['Accept'] = invokeConfig.acceptHeader;
    }
    if (config.requiresBasicAuth) {
      headers['Authorization'] = 'Basic ' + new Buffer.from(invokeConfig.username + ':' + invokeConfig.password).toString('base64');
    }
    if ((config.requiresOauth || config.openIdConnectUrl) && invokeConfig.authToken && invokeConfig.authToken['access_token']) {
      headers['Authorization'] = 'Bearer ' + invokeConfig.authToken['access_token'];
    }
    if (config.requiresHttpBearerToken) {
      headers['Authorization'] = 'Bearer ' + invokeConfig.httpBearerToken;
    }
    if (config.requiresDigestAuth) {
      let digestFields = [];
      if (invokeConfig.httpDigestAuth.digestUsername !== null && invokeConfig.httpDigestAuth.digestUsername !== '') {
        digestFields.push(` username="${invokeConfig.httpDigestAuth.digestUsername}"`);
      }
      if (invokeConfig.httpDigestAuth.digestRealm !== '') {
        digestFields.push(` realm="${invokeConfig.httpDigestAuth.digestRealm}"`);
      }
      if (invokeConfig.httpDigestAuth.digestNonce !== '') {
        digestFields.push(` nonce="${invokeConfig.httpDigestAuth.digestNonce}"`);
      }
      if (invokeConfig.httpDigestAuth.digestOpaque !== '') {
        digestFields.push(` opaque="${invokeConfig.httpDigestAuth.digestOpaque}"`);
      }
      if (invokeConfig.httpDigestAuth.digestResponse !== '') {
        digestFields.push(` response="${invokeConfig.httpDigestAuth.digestResponse}"`);
      }
      headers['Authorization'] = 'Digest' + digestFields.toString();
    }
    if (config.requiresHobaAuth) {
      let hobaFields = [];
      if (invokeConfig.httpHobaAuth.hobaKeyIdentifier !== '') {
        hobaFields.push(invokeConfig.httpHobaAuth.hobaKeyIdentifier);
      }
      if (invokeConfig.httpHobaAuth.hobaChallenge !== '') {
        hobaFields.push(invokeConfig.httpHobaAuth.hobaChallenge);
      }
      if (invokeConfig.httpHobaAuth.hobaNonce !== '') {
        hobaFields.push(invokeConfig.httpHobaAuth.hobaNonce);
      }
      if (invokeConfig.httpHobaAuth.hobaSignature !== '') {
        hobaFields.push(invokeConfig.httpHobaAuth.hobaSignature);
      }
      headers['Authorization'] =
        'HOBA result="' + hobaFields[0].toString() + '.' + hobaFields[1].toString() + '.' + hobaFields[2].toString() + '.' + hobaFields[3].toString() + '"';
    }
    if (config.requiresMutualAuth) {
      let mutualFields = [];
      if (invokeConfig.httpMutualAuth.mutualVersion !== '') {
        mutualFields.push(` version=${invokeConfig.httpMutualAuth.mutualVersion}`);
      }
      if (invokeConfig.httpMutualAuth.mutualSid !== '') {
        mutualFields.push(` sid=${invokeConfig.httpMutualAuth.mutualSid}`);
      }
      if (invokeConfig.httpMutualAuth.mutualNonce !== '') {
        mutualFields.push(` nc=${invokeConfig.httpMutualAuth.mutualNonce}`);
      }
      if (invokeConfig.httpMutualAuth.mutualVkc !== '') {
        mutualFields.push(` vkc=${invokeConfig.httpMutualAuth.mutualVkc}`);
      }
      headers['Authorization'] = 'Mutual' + mutualFields.toString();
    }
    if (config.requiresNegotiateAuth) {
      let negotiateFields = [];
      if (invokeConfig.httpNegotiateAuth.negotiateGssapiData !== '') {
        negotiateFields.push(invokeConfig.httpNegotiateAuth.negotiateGssapiData);
      }
      headers['Authorization'] = 'Negotiate ' + negotiateFields.toString();
    }
    if (config.requiresVapidAuth) {
      let vapidFields = [];
      if (invokeConfig.httpVapidAuth.vapidJSONWebToken !== '') {
        vapidFields.push(` t=${invokeConfig.httpVapidAuth.vapidJSONWebToken}`);
      }
      if (invokeConfig.httpVapidAuth.vapidJSONWebKey !== '') {
        vapidFields.push(` k=${invokeConfig.httpVapidAuth.vapidJSONWebKey}`);
      }
      headers['Authorization'] = 'vapid' + vapidFields.toString();
    }
    if (config.requiresScramShaAuth) {
      let scramShaFields = [];
      if (invokeConfig.httpScramShaAuth.scramSessionId !== '') {
        scramShaFields.push(` sid=${invokeConfig.httpScramShaAuth.scramSessionId}`);
      }
      if (invokeConfig.httpScramShaAuth.scramEncryptedCreds !== '') {
        scramShaFields.push(` data=${invokeConfig.httpScramShaAuth.scramEncryptedCreds}`);
      }
      headers['Authorization'] = 'SCRAM-SHA-256' + scramShaFields.toString();
    }
    if (config.requiresClientId && config.clientIdLocation === 'header') {
      if (config.clientIdName) {
        headers[config.clientIdName] = invokeConfig.clientId;
      } else {
        headers['X-IBM-Client-Id'] = invokeConfig.clientId;
      }
    }
    if (config.requiresClientSecret && config.clientSecretLocation === 'header') {
      if (config.clientSecretName) {
        headers[config.clientSecretName] = invokeConfig.clientSecret;
      } else {
        headers['X-IBM-Client-Secret'] = invokeConfig.clientSecret;
      }
    }
    if (invokeConfig.soapAction) {
      headers['SOAPAction'] = invokeConfig.soapAction;
    }

    if (invokeConfig.variables) {
      for (const [key, val] of Object.entries(invokeConfig.variables)) {
        endpoint = endpoint.replace('{' + key + '}', val.value);
      }
    }

    let parameters = Object.values(invokeConfig.parameterValues);
    if (!parameters) {
      parameters = [];
    }
    // path parameters
    let pathParameters = parameters.filter(function (parameter) {
      return parameter.in === 'path';
    });
    if (pathParameters.length > 0) {
      pathParameters.forEach(function (parameter) {
        if (invokeConfig.parameterValues[parameter.name].value !== undefined) {
          operationPath = operationPath.replace('{' + parameter.name + '}', this.convertParameterValue(parameter, invokeConfig.type, invokeConfig.version));
        } else if (parameter.schema && parameter.schema.default) {
          operationPath = operationPath.replace('{' + parameter.name + '}', parameter.schema.default);
        } else if (
          parameter.content &&
          parameter.content[Object.keys(parameter.content)[0]].schema &&
          parameter.content[Object.keys(parameter.content)[0]].schema.default
        ) {
          operationPath = operationPath.replace('{' + parameter.name + '}', parameter.content[Object.keys(parameter.content)[0]].schema.default);
        }
      }, this);
    }
    // query parameters
    let queryParameters = parameters.filter(function (parameter) {
      return parameter.in === 'query' && invokeConfig.parameterValues[parameter.name] !== undefined;
    });
    let queryParametersAdded = operationPath.indexOf('?') >= 0;
    if (queryParameters.length > 0) {
      queryParameters.forEach(function (parameter) {
        // exclude 'omit' value optional booleans
        if (
          (parameter.type === 'boolean' || (parameter.schema && parameter.schema.type === 'boolean')) &&
          (invokeConfig.parameterValues[parameter.name] === '__omit' || invokeConfig.parameterValues[parameter.name].value === '__omit')
        ) {
          return;
        }
        // exclude 'omit' value optional enums
        if (
          (parameter.enum || (parameter.schema && parameter.schema.enum)) &&
          !parameter.required &&
          invokeConfig.parameterValues[parameter.name] === '__omit'
        ) {
          return;
        }
        if (invokeConfig.parameterValues[parameter.name].value !== undefined) {
          operationPath += queryParametersAdded ? '&' : '?';
          operationPath += this.convertParameterValue(parameter, invokeConfig.type, invokeConfig.version);
          queryParametersAdded = true;
        } else if (parameter.schema && parameter.schema.default && parameter.required) {
          operationPath += queryParametersAdded ? '&' : '?';
          operationPath += parameter.name + '=' + parameter.schema.default;
          queryParametersAdded = true;
        } else if (
          parameter.content &&
          parameter.content[Object.keys(parameter.content)[0]].schema &&
          parameter.content[Object.keys(parameter.content)[0]].schema.default
        ) {
          operationPath += queryParametersAdded ? '&' : '?';
          operationPath += parameter.name + '=' + parameter.content[Object.keys(parameter.content)[0]].schema.default;
          queryParametersAdded = true;
        }
      }, this);
    }
    if (config.requiresClientId && config.clientIdLocation === 'query') {
      let idName;
      if (config.clientIdName) {
        idName = config.clientIdName;
      } else {
        idName = 'client_id';
      }
      if (queryParametersAdded) {
        operationPath += '&' + idName + '=' + invokeConfig.clientId;
      } else {
        operationPath += '?' + idName + '=' + invokeConfig.clientId;
        queryParametersAdded = true;
      }
    }
    // other apiKey query parameters
    if (config.externalApiKeys) {
      Object.keys(config.externalApiKeys).forEach(function (apiKey) {
        if (config.externalApiKeys[apiKey].in === 'query') {
          if (queryParametersAdded) {
            operationPath += '&' + config.externalApiKeys[apiKey].name + '=' + invokeConfig.apiKeys[apiKey].value;
          } else {
            operationPath += '?' + config.externalApiKeys[apiKey].name + '=' + invokeConfig.apiKeys[apiKey].value;
            queryParametersAdded = true;
          }
        }
      });
    }
    if (config.requiresClientSecret && config.clientSecretLocation === 'query') {
      let secretName;
      if (config.clientSecretName) {
        secretName = config.clientSecretName;
      } else {
        secretName = 'client_secret';
      }
      if (queryParametersAdded) {
        operationPath += '&' + secretName + '=' + invokeConfig.clientSecret;
      } else {
        operationPath += '?' + secretName + '=' + invokeConfig.clientSecret;
        queryParametersAdded = true;
      }
    }

    // headers
    let headerParameters = parameters.filter(function (parameter) {
      return parameter.in === 'header' && invokeConfig.parameterValues[parameter.name] !== undefined;
    });
    if (headerParameters.length > 0) {
      headerParameters.forEach(function (parameter) {
        // exclude 'omit' value optional booleans
        if (
          (parameter.type === 'boolean' || (parameter.schema && parameter.schema.type === 'boolean')) &&
          invokeConfig.parameterValues[parameter.name] === '__omit'
        ) {
          return;
        }
        // exclude 'omit' value optional enums
        if (
          (parameter.enum || (parameter.schema && parameter.schema.enum)) &&
          !parameter.required &&
          invokeConfig.parameterValues[parameter.name] === '__omit'
        ) {
          return;
        }
        if (invokeConfig.parameterValues[parameter.name].value !== undefined && String(invokeConfig.parameterValues[parameter.name].value).length > 0) {
          headers[parameter.name] = this.convertParameterValue(parameter, invokeConfig.type, invokeConfig.version);
        } else if (parameter.schema && parameter.schema.default && parameter.required) {
          headers[parameter.name] = parameter.schema.default;
        } else if (
          parameter.content &&
          parameter.content[Object.keys(parameter.content)[0]].schema &&
          parameter.content[Object.keys(parameter.content)[0]].schema.default
        ) {
          headers[parameter.name] = parameter.content[Object.keys(parameter.content)[0]].schema.default;
        }
      }, this);
    }

    // cookies
    let cookieParameters = parameters.filter(function (parameter) {
      return parameter.in === 'cookie' && invokeConfig.parameterValues[parameter.name] !== undefined;
    });
    let cookieAdded = false;
    if (cookieParameters.length > 0) {
      let val = '';
      cookieParameters.forEach(function (parameter) {
        // exclude 'omit' value optional booleans
        if (
          (parameter.type === 'boolean' || (parameter.schema && parameter.schema.type === 'boolean')) &&
          invokeConfig.parameterValues[parameter.name] === '__omit'
        ) {
          return;
        }
        // exclude 'omit' value optional enums
        if (
          (parameter.enum || (parameter.schema && parameter.schema.enum)) &&
          !parameter.required &&
          invokeConfig.parameterValues[parameter.name] === '__omit'
        ) {
          return;
        }
        if (invokeConfig.parameterValues[parameter.name].value !== undefined && String(invokeConfig.parameterValues[parameter.name].value).length > 0) {
          val += cookieAdded ? ';' : '';
          val += this.convertParameterValue(parameter, invokeConfig.type, invokeConfig.version);
          cookieAdded = true;
        } else if (parameter.schema && parameter.schema.default && parameter.required) {
          val += cookieAdded ? ';' : '';
          val += parameter.schema.default;
          cookieAdded = true;
        } else if (
          parameter.content &&
          parameter.content[Object.keys(parameter.content)[0]].schema &&
          parameter.content[Object.keys(parameter.content)[0]].schema.default
        ) {
          val += cookieAdded ? ';' : '';
          val += parameter.content[Object.keys(parameter.content)[0]].schema.default;
          cookieAdded = true;
        }
      }, this);
      if (val !== '') {
        headers['Cookie'] = val;
      }
    }

    // other apiKey header parameters
    if (config.externalApiKeys) {
      Object.keys(config.externalApiKeys).forEach(function (apiKey) {
        if (config.externalApiKeys[apiKey].in === 'header') {
          headers[config.externalApiKeys[apiKey].name] = invokeConfig.apiKeys[apiKey].value;
        }
      });
    }

    // body
    let bodyParameters = parameters.filter(function (parameter) {
      return parameter.in === 'body';
    });
    // need to know the formdata params too
    let formDataParameters = parameters.filter(function (parameter) {
      return parameter.in === 'formData';
    });
    if (bodyParameters.length > 0 && bodyParameters[0].value !== undefined) {
      // use the first one only
      invokeConfig.body = invokeConfig.parameterValues[bodyParameters[0].name].value;
    } else {
      delete invokeConfig.body;
      // delete any content-type header unless we have formdata params
      if (!formDataParameters || formDataParameters.length === 0) {
        delete headers['Content-Type'];
      }
    }

    // form data
    let formBody;
    if (formDataParameters.length > 0) {
      formBody = '';

      if (invokeConfig.contentTypeHeader.startsWith('multipart/')) {
        // we potentially have a combination of files and key=value parameters
        // which makes things complicated...

        // first we need a boundary string
        let boundaryString = 'testToolFormDataBoundary';
        invokeConfig.contentTypeHeader = invokeConfig.contentTypeHeader + '; boundary=' + boundaryString;
        headers['Content-Type'] = invokeConfig.contentTypeHeader;
        // now we can start building the form body
        formDataParameters.forEach((parameter) => {
          if (parameter.value === undefined) {
            return;
          }
          if (
            (parameter?.type === 'file' || (parameter.type === 'string' && (parameter.format === 'binary' || parameter.format === 'base64'))) &&
            invokeConfig.parameterValues[parameter.name].value[0]?.fileContents
          ) {
            let file = invokeConfig.parameterValues[parameter.name].value[0].file;
            let contents = invokeConfig.parameterValues[parameter.name].value[0].fileContents;
            formBody += '--' + boundaryString + '\n';
            formBody += this.handleFile(parameter, file, contents);
          } else if (
            parameter.type === 'array' &&
            (parameter.items?.type === 'file' ||
              (parameter.items?.type === 'string' && (parameter.items?.format === 'binary' || parameter.items?.format === 'base64')))
          ) {
            formBody += '--' + boundaryString + '\n';
            formBody += '[';
            let val = invokeConfig.parameterValues[parameter.name].value?.map((val) => {
              let file = val?.file;
              let contents = val?.fileContents;

              return this.handleFile(parameter, file, contents);
            });
            formBody += val.join();
            formBody += ']\n';
          } else if (invokeConfig.parameterValues[parameter.name].value) {
            formBody += '--' + boundaryString + '\n';
            if (parameter.encoding && parameter.encoding.contentType) {
              formBody += 'Content-Type: ' + parameter.encoding.contentType + '\n';
            }

            let addedContentDisposition = false;
            if (parameter.encoding && parameter.encoding.headers) {
              for (const [key, val] of Object.entries(parameter.encoding.headers)) {
                if (key.toLowerCase() !== 'content-type') {
                  formBody += key + ': ' + val + '\n';
                  if (key.toLowerCase() === 'content-disposition') {
                    addedContentDisposition = true;
                  }
                }
              }
            }
            if (!addedContentDisposition) {
              formBody += 'Content-Disposition: form-data; name="' + parameter.name + '"\n';
            }
            if (invokeConfig.contentTypeHeader === 'multipart/form-data') {
              formBody += this.convertParameterValue(parameter, invokeConfig.type, invokeConfig.version, invokeConfig.contentTypeHeader) + '\n';
            } else {
              formBody += invokeConfig.parameterValues[parameter.name].value + '\n';
            }
            formBody += '\n';
          }
        });
        if (formBody !== '') {
          // add the closing boundary
          formBody += '--' + boundaryString + '--';
        }
      } else {
        formDataParameters.forEach(function (parameter) {
          if (parameter.value === undefined) {
            return;
          } else if (invokeConfig.contentTypeHeader === 'application/x-www-form-urlencoded') {
            formBody += this.convertParameterValue(parameter, invokeConfig.type, invokeConfig.version, invokeConfig.contentTypeHeader) + '&';
          } else {
            formBody += parameter.name + '=' + invokeConfig.parameterValues[parameter.name].value + '&';
          }
        }, this);

        formBody = formBody.substring(0, formBody.length - 1);
      }
    }
    if (formBody) {
      invokeConfig.body = formBody;
    }
    if (endpoint.endsWith('/') && operationPath.startsWith('/')) {
      endpoint = endpoint.substr(0, endpoint.length - 1);
    }

    invokeConfig.targetUrl = endpoint + operationPath;
    invokeConfig.headers = headers;
    return invokeConfig;
  }

  invoke(invokeConfig, config) {
    let repeatCount = 1;
    if (invokeConfig.repeater && invokeConfig.repeater.repeat === true) {
      repeatCount = invokeConfig.repeater.count;
    }
    invokeConfig = this.fillInvokeConfig(invokeConfig, config);
    this.stopInvoke = false;

    return new Promise((resolve, reject) => {
      this.invokeHelper(invokeConfig, 0, repeatCount, 0, resolve, reject);
    });
  }

  invokeWebsocket(invokeConfig, config) {
    invokeConfig = this.fillInvokeConfig(invokeConfig, config);

    let protocol = invokeConfig.parameterValues[Object.keys(invokeConfig.parameterValues).find((key) => key.toLowerCase() === 'sec-websocket-protocol')];
    protocol = get(protocol, 'value');
    try {
      let parsedProtocol = JSON.parse(protocol);
      if (Array.isArray(parsedProtocol)) {
        protocol = parsedProtocol;
      }
    } catch (e) {
      //Do nothing
    }
    let ws;
    try {
      ws = new WebSocket(invokeConfig.targetUrl, protocol);
    } catch (e) {
      console.error(e);
    }
    return ws;
  }

  authorize(config, clientId, scopes, redirectUri) {
    if (!config) {
      console.error('missing configuration for authorize call.');
      return; // TODO: Maybe this should throw?
    }

    if (config.oauthFlow === 'accessCode' || config.oauthFlow === 'authorizationCode') {
      let url =
        config.selectedAuthUrl + '?response_type=code&client_id=' + clientId + '&redirect_uri=' + redirectUri + '&scope=' + this._getScopesAsString(scopes);
      window.open(url, '_blank', 'noopener');
    } else if (config.oauthFlow === 'implicit') {
      let url =
        config.selectedAuthUrl + '?response_type=token&client_id=' + clientId + '&redirect_uri=' + redirectUri + '&scope=' + this._getScopesAsString(scopes);
      window.open(url, '_blank', 'noopener');
    }
  }

  getToken(config, authToken, clientId, clientSecret, scopes, redirectUri, username, password) {
    if (config.oauthFlow === 'password') {
      let req = {
        method: 'POST',
        body:
          'grant_type=password&username=' +
          username +
          '&password=' +
          encodeURIComponent(password) +
          '&client_id=' +
          clientId +
          '&client_secret=' +
          clientSecret +
          '&scope=' +
          this._getScopesAsString(scopes),
        url: config.selectedTokenUrl,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        cache: 'no-store'
      };
      return fetch(req.url, req)
        .then(async (res) => {
          await this.handleError(res); // this will throw an exception if !ok.
          return res;
        })
        .then((res) => res.json());
    } else if (config.oauthFlow === 'application' || config.oauthFlow === 'clientCredentials') {
      let req = {
        method: 'POST',
        body: 'grant_type=client_credentials&scope=' + this._getScopesAsString(scopes),
        url: config.selectedTokenUrl,
        headers: {
          Authorization: 'Basic ' + new Buffer.from(clientId + ':' + clientSecret).toString('base64'),
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        cache: 'no-store'
      };
      return fetch(req.url, req)
        .then(async (res) => {
          await this.handleError(res); // this will throw an exception if !ok.
          return res;
        })
        .then((res) => res.json());
    } else {
      let req = {
        method: 'POST',
        url: config.selectedTokenUrl,
        headers: new Headers({
          Authorization: 'Basic ' + new Buffer.from(clientId + ':' + clientSecret).toString('base64'),
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
        cache: 'no-store',
        body:
          'grant_type=authorization_code&code=' +
          encodeURIComponent(authToken['code']) +
          '&redirect_uri=' +
          redirectUri +
          '&scope=' +
          this._getScopesAsString(scopes)
      };
      return fetch(req.url, req)
        .then(async (res) => {
          await this.handleError(res); // this will throw an exception if !ok.
          return res;
        })
        .then((res) => res.json());
    }
  }

  refreshToken(oauthRefreshUrl, authToken, clientId, clientSecret) {
    let req = {
      method: 'POST',
      url: oauthRefreshUrl,
      body: 'grant_type=refresh_token&refresh_token=' + authToken['refresh_token'],
      headers: new Headers({
        Authorization: 'Basic ' + new Buffer.from(clientId + ':' + clientSecret).toString('base64'),
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      cache: 'no-store'
    };

    return fetch(req.url, req)
      .then(async (res) => {
        await this.handleError(res); // this will throw an exception if !ok.
        return res;
      })
      .then((res) => res.json());
  }

  discover(openIdConnectUrl) {
    let req = {
      method: 'GET',
      url: openIdConnectUrl,
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    };

    return fetch(req.url, req)
      .then(async (res) => {
        await this.handleError(res); // this will throw an exception if !ok.
        return res;
      })
      .then((res) => res.json());
  }

  async handleError(response) {
    if (response.ok) {
      return response;
    }

    let parsedError = new Error();
    if (typeof response.json === 'function') {
      parsedError['data'] = (await response.json()) || {};
    } else if (typeof response.text === 'function') {
      parsedError['text'] = (await response.text()) || '';
    }

    if (response.status) {
      parsedError['status'] = response.status;
    }

    if (response.statusText) {
      parsedError['statusText'] = response.statusText;
    }

    throw parsedError;
  }

  _getScopesAsString(scopes) {
    if (!scopes) {
      return '';
    }
    let selected = [];
    Object.keys(scopes).forEach(function (scope) {
      if (scopes[scope]) {
        selected.push(scope);
      }
    });
    return selected.join(' ');
  }
}
